import { useEffect, useRef, useState } from "react";

import { NavBar } from "@/components/client-navigation-bar";
import { HeroBackdrop } from "@/components/home/hero-backdrop";
import { NowPlayingCard } from "@/components/home/now-playing-card";
import { UpNextPanel } from "@/components/home/up-next-panel";
import { usePlayer } from "@/components/providers/player-provider";
import type { OnAirEpisode } from "@/lib/episodes";
import { findCurrentlyAiringEpisode, getTodayEpisodes } from "@/lib/episodes";
import type { ResultData } from "@/lib/metadata";
import type { Page, SiteSetting } from "@/lib/types";
import { cn } from "@/lib/utils";

const useScrolledPast = (target: React.RefObject<HTMLElement | null>) => {
  const [scrolledPast, setScrolledPast] = useState(false);

  useEffect(() => {
    const node = target.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setScrolledPast(
        entry !== undefined &&
          !entry.isIntersecting &&
          entry.boundingClientRect.top < 0
      );
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [target]);

  return scrolledPast;
};

export function Hero(props: {
  pages: Page[];
  settings: SiteSetting[];
  metadata: ResultData | null;
  initialEpisodes: OnAirEpisode[];
}) {
  const [todayEpisodes, setTodayEpisodes] = useState<OnAirEpisode[]>(
    props.initialEpisodes
  );
  const [episode, setEpisode] = useState<OnAirEpisode | null>(() =>
    findCurrentlyAiringEpisode(props.initialEpisodes)
  );
  const { current, playLive, playing, toggle } = usePlayer();
  const heroNav = useRef<HTMLDivElement>(null);
  const navStuck = useScrolledPast(heroNav);

  useEffect(() => {
    const refresh = async () => {
      setTodayEpisodes(await getTodayEpisodes());
    };

    void refresh();

    const id = setInterval(
      () => {
        void refresh();
      },
      1000 * 60 * 5
    );

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const refresh = () => setEpisode(findCurrentlyAiringEpisode(todayEpisodes));

    refresh();

    const id = setInterval(refresh, 1000 * 30);

    return () => clearInterval(id);
  }, [todayEpisodes]);

  const offline = !episode && props.metadata?.offline;
  const livePlaying = current?.kind === "live" && playing;

  return (
    <>
      <NavBar
        aria-hidden={!navStuck}
        className={cn(
          "fixed inset-x-0 transition-transform duration-200 ease-out motion-reduce:transition-none",
          !navStuck && "-translate-y-full"
        )}
        inert={!navStuck}
        pages={props.pages}
      />
      <header className="bg-primary text-primary-foreground relative isolate flex min-h-[75dvh] flex-col overflow-hidden">
        <HeroBackdrop />

        <div className="relative flex flex-1 flex-col">
          <div ref={heroNav}>
            <NavBar pages={props.pages} variant="hero" />
          </div>

          <div className="mx-auto grid w-full max-w-7xl flex-1 grid-cols-1 content-center gap-8 px-6 py-10 lg:grid-cols-[1.6fr_1fr] lg:px-10">
            <div>
              <NowPlayingCard
                disabled={offline}
                episode={episode}
                metadata={props.metadata}
                onToggle={() => {
                  if (current?.kind === "live") {
                    toggle();
                  } else {
                    playLive();
                  }
                }}
                playing={livePlaying}
              />
            </div>

            {!offline && <UpNextPanel episodes={todayEpisodes} />}
          </div>
        </div>
      </header>
    </>
  );
}
